import { ClipboardList, Briefcase, FileCheck2, AlertOctagon } from 'lucide-react';
import type { SlaSummary } from '@/types';

interface SlaSummaryCardsProps {
  summary: SlaSummary;
}

interface CardData {
  title: string;
  icon: typeof ClipboardList;
  iconBg: string;
  iconColor: string;
  active: number;
  dueSoon: number;
  overdue: number;
  // Optional extra row (used by fieldwork for reminderPending).
  extraLabel?: string;
  extraValue?: number;
}

export default function SlaSummaryCards({ summary }: SlaSummaryCardsProps) {
  const cards: CardData[] = [
    {
      title: 'Assignment',
      icon: ClipboardList,
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      active: summary.assignment.active,
      dueSoon: summary.assignment.dueSoon,
      overdue: summary.assignment.overdue,
    },
    {
      title: 'Fieldwork',
      icon: Briefcase,
      iconBg: 'bg-orange-50',
      iconColor: 'text-[#FF6B35]',
      active: summary.fieldwork.active,
      dueSoon: summary.fieldwork.dueSoon,
      overdue: summary.fieldwork.overdue,
      extraLabel: 'Reminder pending',
      extraValue: summary.fieldwork.reminderPending,
    },
    {
      title: 'Review',
      icon: FileCheck2,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      active: summary.review.active,
      dueSoon: summary.review.dueSoon,
      overdue: summary.review.overdue,
    },
  ];

  const totalOverdue =
    summary.assignment.overdue + summary.fieldwork.overdue + summary.review.overdue;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.title}
            className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`${card.iconBg} p-2.5 rounded-xl`}>
                <Icon className={`w-5 h-5 ${card.iconColor}`} />
              </div>
              <h3 className="text-base font-semibold text-gray-900">{card.title}</h3>
            </div>
            <div className="flex items-end gap-1 mb-3">
              <span className="text-3xl font-bold text-gray-900 leading-none">
                {card.active}
              </span>
              <span className="text-xs text-gray-500 mb-1">active</span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span
                className={`px-2 py-0.5 rounded-md font-semibold ${
                  card.dueSoon > 0
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-gray-50 text-gray-500'
                }`}
              >
                {card.dueSoon} due soon
              </span>
              <span
                className={`px-2 py-0.5 rounded-md font-semibold ${
                  card.overdue > 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-500'
                }`}
              >
                {card.overdue} overdue
              </span>
            </div>
            {card.extraLabel && (
              <p className="mt-3 text-[11px] text-gray-500">
                {card.extraLabel}:{' '}
                <span className="font-semibold text-gray-700">{card.extraValue}</span>
              </p>
            )}
          </div>
        );
      })}

      {/* Total breach card — quick at-a-glance attention magnet */}
      <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-red-50 p-2.5 rounded-xl">
            <AlertOctagon className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">Total Overdue</h3>
        </div>
        <div className="flex items-end gap-1 mb-3">
          <span className="text-3xl font-bold text-gray-900 leading-none">{totalOverdue}</span>
          <span className="text-xs text-gray-500 mb-1">past deadline</span>
        </div>
        <p className="text-[11px] text-gray-500">
          Across assignment, fieldwork, and review stages.
        </p>
      </div>
    </div>
  );
}
